import { ElMessage, ElMessageBox } from 'element-plus'

const defaultConfirmOptions = {
  confirmButtonText: '确定',
  cancelButtonText: '取消',
  closeOnClickModal: false,
  closeOnPressEscape: true
}

function normalizeMessage(message) {
  if (message === undefined || message === null) return ''
  return String(message)
}

function ensureConfirmStyle() {
  if (typeof document === 'undefined' || document.getElementById('ui-custom-confirm-style')) return
  const style = document.createElement('style')
  style.id = 'ui-custom-confirm-style'
  style.textContent = `
    .ui-custom-confirm-overlay {
      position: fixed;
      inset: 0;
      z-index: 5000;
      display: grid;
      place-items: center;
      padding: 20px;
      background: rgba(0, 24, 66, 0.34);
      backdrop-filter: blur(4px);
    }
    .ui-custom-confirm-modal {
      width: min(420px, calc(100vw - 32px));
      padding: 22px;
      border-radius: 20px;
      border: 1px solid rgba(0, 59, 255, 0.14);
      background: #fff;
      color: #0f172a;
      box-shadow: 0 0 4.7px rgba(0, 0, 0, 0.15), 0 18px 36px rgba(0, 60, 255, 0.12);
    }
    .ui-custom-confirm-modal h3 {
      margin: 0 0 14px;
      color: #003CFF;
      font-size: 20px;
      font-weight: 700;
      line-height: 1.35;
    }
    .ui-custom-confirm-message {
      margin: 0;
      color: #0f172a;
      font-size: 15px;
      line-height: 1.65;
      white-space: pre-line;
      word-break: break-word;
    }
    .ui-custom-confirm-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      margin-top: 20px;
    }
    .ui-custom-confirm-btn {
      min-width: 96px;
      height: 40px;
      padding: 0 18px;
      border: none;
      border-radius: 999px;
      font-size: 13px;
      font-weight: 700;
      cursor: pointer;
    }
    .ui-custom-confirm-btn--cancel {
      background: #CEE0FD;
      color: #003CFF;
    }
    .ui-custom-confirm-btn--confirm {
      background: #003EFF;
      color: #fff;
      box-shadow: 0 5px 8.2px rgba(0, 60, 255, 0.24);
    }
    .ui-custom-confirm-modal.is-warning .ui-custom-confirm-btn--confirm {
      background: #D88418;
      box-shadow: 0 5px 8.2px rgba(216, 132, 24, 0.24);
    }
    .ui-custom-confirm-modal.is-danger .ui-custom-confirm-btn--confirm,
    .ui-custom-confirm-modal.is-error .ui-custom-confirm-btn--confirm {
      background: #dc2626;
      box-shadow: 0 5px 8.2px rgba(220, 38, 38, 0.22);
    }
    @media (max-width: 640px) {
      .ui-custom-confirm-overlay {
        padding: 10px;
      }
      .ui-custom-confirm-modal {
        width: calc(100vw - 20px);
        max-height: calc(100dvh - 20px);
        overflow-y: auto;
        padding: 14px;
        border-radius: 10px;
      }
      .ui-custom-confirm-modal h3 {
        position: relative;
        display: inline-flex;
        margin-bottom: 18px;
        color: #000;
        font-size: 18px;
      }
      .ui-custom-confirm-modal h3::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: -7px;
        height: 4px;
        border-radius: 3px;
        background: #003EFF;
        box-shadow: 0 1px 3.5px rgba(0, 221, 255, 0.3);
      }
      .ui-custom-confirm-message {
        font-size: 16px;
      }
      .ui-custom-confirm-actions {
        flex-direction: column-reverse;
      }
      .ui-custom-confirm-btn {
        width: 100%;
        min-height: 44px;
        border-radius: 5px;
        font-size: 16px;
        letter-spacing: 0.08em;
      }
    }
  `
  document.head.appendChild(style)
}

export function uiAlert(message, options = {}) {
  return ElMessage({
    message: normalizeMessage(message),
    type: options.type || 'info',
    showClose: true,
    grouping: true,
    duration: options.duration ?? 2400
  })
}

export async function uiConfirm(message, options = {}) {
  if (typeof document === 'undefined') return false
  ensureConfirmStyle()
  const merged = { ...defaultConfirmOptions, type: 'warning', ...options }
  return new Promise(resolve => {
    const overlay = document.createElement('div')
    overlay.className = 'ui-custom-confirm-overlay'
    const modal = document.createElement('div')
    modal.className = `ui-custom-confirm-modal is-${merged.type || 'warning'}`
    modal.setAttribute('role', 'dialog')
    modal.setAttribute('aria-modal', 'true')

    const title = document.createElement('h3')
    title.textContent = merged.title || '确认'
    const body = document.createElement('p')
    body.className = 'ui-custom-confirm-message'
    body.textContent = normalizeMessage(message)
    const actions = document.createElement('div')
    actions.className = 'ui-custom-confirm-actions'
    const cancelButton = document.createElement('button')
    cancelButton.type = 'button'
    cancelButton.className = 'ui-custom-confirm-btn ui-custom-confirm-btn--cancel'
    cancelButton.textContent = merged.cancelButtonText || '取消'
    const confirmButton = document.createElement('button')
    confirmButton.type = 'button'
    confirmButton.className = 'ui-custom-confirm-btn ui-custom-confirm-btn--confirm'
    confirmButton.textContent = merged.confirmButtonText || '确定'

    actions.append(cancelButton, confirmButton)
    modal.append(title, body, actions)
    overlay.appendChild(modal)

    let settled = false
    const cleanup = result => {
      if (settled) return
      settled = true
      document.removeEventListener('keydown', onKeydown)
      overlay.remove()
      resolve(result)
    }
    const onKeydown = event => {
      if (event.key === 'Escape' && merged.closeOnPressEscape !== false) cleanup(false)
    }
    cancelButton.addEventListener('click', () => cleanup(false))
    confirmButton.addEventListener('click', () => cleanup(true))
    overlay.addEventListener('click', event => {
      if (event.target === overlay && merged.closeOnClickModal) cleanup(false)
    })
    document.addEventListener('keydown', onKeydown)
    document.body.appendChild(overlay)
    confirmButton.focus()
  })
}

export async function uiPrompt(message, value = '', options = {}) {
  try {
    const { value: inputValue } = await ElMessageBox.prompt(
      normalizeMessage(message),
      options.title || '请输入',
      {
        ...defaultConfirmOptions,
        inputValue: value ?? '',
        inputPlaceholder: options.inputPlaceholder || '请输入',
        ...options
      }
    )
    return inputValue
  } catch (_) {
    return null
  }
}

export function showComingSoon() {
  return uiAlert('功能开放中', { type: 'info' })
}

export function showComingSoonDialog(message = '功能正在开发中') {
  return ElMessageBox.alert(
    normalizeMessage(message),
    '提示',
    {
      confirmButtonText: '知道了',
      closeOnClickModal: true,
      closeOnPressEscape: true,
      showClose: true,
      type: 'info'
    }
  )
}

export function installDialogGlobals(app) {
  app.config.globalProperties.$uiAlert = uiAlert
  app.config.globalProperties.$uiConfirm = uiConfirm
  app.config.globalProperties.$uiPrompt = uiPrompt
  app.config.globalProperties.$showComingSoon = showComingSoon
  app.config.globalProperties.$showComingSoonDialog = showComingSoonDialog

  window.alert = message => {
    uiAlert(message)
  }
}
